import React, { useState, useEffect, useCallback } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

import { leadsApi, Lead, LeadStage, goalsApi, Goal } from '../services/api';
import { Column } from './Column';
import { LeadFormModal } from './LeadFormModal';
import { LeadDetailsModal } from './LeadDetailsModal';
import { ConvertWonLeadModal } from './ConvertWonLeadModal';
import { ShareProposalModal } from './ShareProposalModal';
import { ExcelImportModal, ImportFieldDef } from './ExcelImportModal';
import { exportToExcel } from '../utils/excel';
import { Plus, RefreshCw, Mail, X, FileSpreadsheet, FileDown } from 'lucide-react';
import toast from 'react-hot-toast';

const LEAD_IMPORT_FIELDS: ImportFieldDef[] = [
  { key: 'name', label: 'Nome', required: true, aliases: ['nome', 'name', 'cliente', 'nomecompleto'], example: 'João Mário Manuel' },
  { key: 'email', label: 'Email', required: true, aliases: ['email', 'correio', 'mail', 'email'], example: 'joao.manuel@empresa.com' },
  { key: 'phone', label: 'Telefone', aliases: ['telefone', 'phone', 'celular', 'telemovel', 'contacto', 'whatsapp'], example: '+258 84 123 4567' },
  { key: 'stage', label: 'Estágio', aliases: ['estagio', 'stage', 'fase', 'etapa'], example: 'prospecting' },
  { key: 'source', label: 'Origem', aliases: ['origem', 'source', 'fonte'], example: 'referral' },
  { key: 'dealValue', label: 'Valor (MT)', aliases: ['valor', 'valordonegocio', 'montante', 'dealvalue', 'preco', 'orcamento'], example: '150000' },
  { key: 'expectedCloseDate', label: 'Data fecho', aliases: ['datadefecho', 'fecho', 'expectedclosedate', 'dataprevista', 'dataprevisao'], example: '2026-12-15' },
  { key: 'lostReason', label: 'Motivo perda', aliases: ['motivoperda', 'lostreason', 'razaoperda'], example: '' },
  { key: 'notes', label: 'Notas', aliases: ['notas', 'notes', 'observacoes', 'obs'], example: 'Interessado no plano empresarial' },
];

const STAGES: { id: LeadStage; label: string }[] = [
  { id: 'new', label: 'Prospecção' },
  { id: 'prospecting', label: 'Prospecção Avançada' },
  { id: 'contacted', label: 'Contactados' },
  { id: 'negotiation', label: 'Em Negociação' },
  { id: 'proposal', label: 'Proposta Enviada' },
  { id: 'pending', label: 'Aguardando' },
  { id: 'won', label: 'Fechado Ganho' },
  { id: 'lost', label: 'Fechado Perdido' },
];

export const LeadsBoard: React.FC = () => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingLead, setEditingLead] = useState<Lead | null>(null);

  // Seleção múltipla de leads
  const [selectedLeadIds, setSelectedLeadIds] = useState<Set<string>>(new Set());

  const [showProposalModal, setShowProposalModal] = useState(false);

  // Detalhes do lead + importação Excel
  const [detailsLead, setDetailsLead] = useState<Lead | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [goalTarget, setGoalTarget] = useState<number | undefined>(undefined);

  // Modal de conversão em venda ao Fechar Ganho
  const [wonModalLead, setWonModalLead] = useState<Lead | null>(null);

  const selectedLeadsForProposal = leads.filter((lead) =>
    selectedLeadIds.has(lead._id!)
  );

  const isMobile =
    /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    ) || window.innerWidth <= 768;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // ────────────────────────────────────────────────
  // Carregar leads
  // ────────────────────────────────────────────────
  const loadLeads = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await leadsApi.getAll({});
      const activeLeads = data.filter((lead: Lead) => !lead.convertedTo);
      setLeads(activeLeads);
    } catch (err: any) {
      console.error('Erro ao carregar leads:', err);
      setError('Não foi possível carregar o pipeline. Tente novamente.');
      toast.error('Falha ao carregar leads');
    } finally {
      setLoading(false);
    }
  }, []);

  // Busca a meta activa do período para comparar com a previsão dos leads
  const loadActiveGoal = useCallback(async () => {
    try {
      const goals = await goalsApi.getAll(undefined, 'active');
      const now = new Date();
      const active = goals.find(
        (g: Goal) => new Date(g.startDate) <= now && new Date(g.endDate) >= now
      );
      setGoalTarget(active ? Number(active.financialTarget) || Number(active.monthlyTarget) || 0 : undefined);
    } catch (err) {
      console.warn('Não foi possível carregar a meta activa:', err);
      setGoalTarget(undefined);
    }
  }, []);

  useEffect(() => {
    loadLeads();
    loadActiveGoal();
  }, [loadLeads, loadActiveGoal]);

  useEffect(() => {
    const handler = () => loadLeads();
    window.addEventListener('workspaceChanged', handler);
    return () => window.removeEventListener('workspaceChanged', handler);
  }, [loadLeads]);

  // ────────────────────────────────────────────────
  // Handlers de drag & drop
  // ────────────────────────────────────────────────
  const handleDragEnd = async (event: any) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const leadId = active.id as string;
    const newStage = over.id as LeadStage;

    const targetLead = leads.find((l) => l._id === leadId);
    if (newStage === 'won' && targetLead) {
      setWonModalLead(targetLead);
      return;
    }

    // Atualização otimista
    setLeads((prev) =>
      prev.map((lead) =>
        lead._id === leadId ? { ...lead, stage: newStage } : lead
      )
    );

    try {
      await leadsApi.update(leadId, { stage: newStage });
      toast.success(`Lead movido para "${STAGES.find((s) => s.id === newStage)?.label}"`);
    } catch (err: any) {
      console.error('Falha ao mover lead:', err);
      toast.error('Não foi possível mover o lead');
      loadLeads(); // reverte em caso de erro
    }
  };

  // ────────────────────────────────────────────────
  // Handler para mudança de estágio via select no card
  // ────────────────────────────────────────────────
  const handleStageChange = async (leadId: string, newStage: LeadStage) => {
    const targetLead = leads.find((l) => l._id === leadId);
    if (newStage === 'won' && targetLead) {
      setWonModalLead(targetLead);
      return;
    }

    try {
      setLeads((prevLeads) =>
        prevLeads.map((lead) =>
          lead._id === leadId ? { ...lead, stage: newStage } : lead
        )
      );
      await leadsApi.update(leadId, { stage: newStage });
      toast.success(`Estágio alterado para "${STAGES.find((s) => s.id === newStage)?.label}"`);
    } catch (err: any) {
      console.error('Erro ao atualizar estágio:', err);
      toast.error('Não foi possível alterar o estágio');
      loadLeads();
    }
  };

  // ────────────────────────────────────────────────
  // Handlers de edição, criação e conversão
  // ────────────────────────────────────────────────
  const handleEdit = (lead: Lead) => {
    setEditingLead(lead);
    setIsModalOpen(true);
  };

  const handleCreateNew = () => {
    setEditingLead(null);
    setIsModalOpen(true);
  };

  const handleConvert = (leadId: string) => {
    const targetLead = leads.find((l) => l._id === leadId);
    if (targetLead) {
      setWonModalLead(targetLead);
    }
  };



  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingLead(null);
  };

  const handleDeleteSuccess = () => {
    loadLeads();
  };

  const handleExportExcel = () => {
    if (leads.length === 0) {
      toast.error('Não existem leads para exportar.');
      return;
    }
    exportToExcel(
      [
        { key: 'name', label: 'Nome' },
        { key: 'email', label: 'Email' },
        { key: 'phone', label: 'Telefone' },
        { key: 'stage', label: 'Estágio' },
        { key: 'source', label: 'Origem' },
        { key: 'dealValue', label: 'Valor (MT)' },
        { key: 'expectedCloseDate', label: 'Data fecho' },
        { key: 'lostReason', label: 'Motivo perda' },
        { key: 'notes', label: 'Notas' },
      ],
      leads,
      `leads-${new Date().toISOString().slice(0, 10)}.xlsx`,
      'Leads'
    );
    toast.success(`${leads.length} leads exportados.`);
  };

  // ────────────────────────────────────────────────
  // Seleção múltipla
  // ────────────────────────────────────────────────
  const handleToggleSelect = (leadId: string) => {
    setSelectedLeadIds((prev) => {
      const next = new Set(prev);
      if (next.has(leadId)) {
        next.delete(leadId);
      } else {
        next.add(leadId);
      }
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedLeadIds(new Set());
  };

  // ────────────────────────────────────────────────
  // Colunas derivadas
  // ────────────────────────────────────────────────
  const columns = STAGES.map((stage) => ({
    ...stage,
    leads: leads.filter((l) => l.stage === stage.id),
  }));

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
     <div className="space-y-6 mb-6">
  {/* Header Section */}
  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center lg:text-left">
      Pipeline de Vendas
    </h1>

    {/* Actions Group */}
    <div className="flex flex-col sm:flex-row flex-wrap items-center gap-3 w-full lg:w-auto">
      {/* Secondary Actions: Grid on mobile, flex on desktop */}
      <div className="grid grid-cols-2 gap-3 w-full sm:w-auto">
        <button
          onClick={loadLeads}
          disabled={loading}
          className="flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 rounded-lg transition disabled:opacity-50 text-sm font-medium"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          <span>Atualizar</span>
        </button>

        <button
          onClick={handleCreateNew}
          className="bg-blue-600 text-white px-4 py-2.5 rounded-lg flex items-center justify-center gap-2 hover:bg-blue-700 transition text-sm font-medium shadow-sm"
        >
          <Plus size={18} />
          <span>Novo Lead</span>
        </button>
      </div>

      {/* Importar/Exportar Excel */}
      <div className="flex gap-2 w-full sm:w-auto">
        <button
          onClick={() => setShowImportModal(true)}
          className="flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition text-sm font-medium shadow-sm flex-1 sm:flex-none"
        >
          <FileSpreadsheet size={16} />
          <span>Importar Excel</span>
        </button>
        <button
          onClick={handleExportExcel}
          className="flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 rounded-lg transition text-sm font-medium shadow-sm flex-1 sm:flex-none"
        >
          <FileDown size={16} />
          <span>Exportar</span>
        </button>
      </div>

      {/* Primary Action: Full width on mobile, auto on tablet+ */}
      <button
        disabled={selectedLeadIds.size === 0}
        onClick={() => {
          if (selectedLeadIds.size === 0) {
            toast.error('Selecione pelo menos um lead');
            return;
          }
          setShowProposalModal(true);
        }}
        className="bg-green-600 text-white px-4 py-2.5 rounded-lg flex items-center justify-center gap-2 hover:bg-green-700 transition text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed shadow-sm w-full sm:w-auto"
      >
        <Mail size={18} />
        <span>
          Enviar Proposta {selectedLeadIds.size > 0 && `(${selectedLeadIds.size})`}
        </span>
      </button>

      {/* Clear Selection: Centered link-style button */}
      {selectedLeadIds.size > 0 && (
        <button
          onClick={clearSelection}
          className="text-sm text-gray-500 hover:text-gray-900 flex items-center justify-center gap-1 py-2 sm:py-0 transition-colors w-full sm:w-auto"
        >
          <X size={16} />
          <span>Limpar seleção</span>
        </button>
      )}
    </div>
  </div>

  {/* Error Message: Spans full width below the header */}
  {error && (
    <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg animate-in fade-in slide-in-from-top-2">
      {error}
    </div>
  )}
</div>

  

      {/* Loading ou conteúdo */}
      {loading && leads.length === 0 ? (
        <div className="text-center py-20 text-gray-500">
          A carregar pipeline...
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <div className="flex gap-4 sm:gap-6 overflow-x-auto pb-8 min-h-[60vh] sm:min-h-[75vh]">
            {columns.map((column) => (
              <Column
                key={column.id}
                stage={column}
                leads={column.leads}
                isLoading={loading}
                isMobile={isMobile}
                onEdit={handleEdit}
                onConvert={handleConvert}
                onDeleteSuccess={handleDeleteSuccess}
                onStageChange={handleStageChange}          
                selectedLeadIds={selectedLeadIds}
                onToggleSelect={handleToggleSelect}
                onViewDetails={setDetailsLead}
                goalTarget={goalTarget}
              />
            ))}
          </div>
        </DndContext>
      )}

      {/* Modal de criação/edição de lead */}
      <LeadFormModal
        isOpen={isModalOpen}
        initialData={editingLead ?? undefined}
        onClose={handleCloseModal}
        onSuccess={() => {
          loadLeads();
        }}
      />

      {/* Modal de envio de proposta */}
      {showProposalModal && (
        <ShareProposalModal
          isOpen={showProposalModal}
          onClose={() => setShowProposalModal(false)}
          initialSelected={selectedLeadsForProposal}
          mode="leads"
        />
      )}

      {/* Modal de mais informações do lead */}
      <LeadDetailsModal
        lead={detailsLead}
        onClose={() => setDetailsLead(null)}
        onEdit={(lead) => {
          setDetailsLead(null);
          handleEdit(lead);
        }}
      />

      {/* Modal de importação Excel */}
      <ExcelImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Importar Leads de Excel"
        entityLabel="lead"
        templateFilename="template-leads.xlsx"
        fieldDefs={LEAD_IMPORT_FIELDS}
        importFn={(items) => leadsApi.import(items)}
        onSuccess={() => loadLeads()}
      />

      {/* Modal de conversão & fecho de venda */}
      <ConvertWonLeadModal
        isOpen={!!wonModalLead}
        lead={wonModalLead}
        onClose={() => setWonModalLead(null)}
        onSuccess={() => {
          setWonModalLead(null);
          loadLeads();
        }}
      />
    </div>
  );
};